/* eslint-disable prettier/prettier */

import { useCallback, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { SystemIcon } from '@monorepo/nova-icons';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useBreakpoints,
} from '@monorepo/nova-ui';
import {
  Account,
  AccountBalance,
  ProfitableDaysResult,
  Program,
} from '@monorepo/types';
import {
  DEFAULT_VALUES,
  OBJECTIVE_ICONS,
  OBJECTIVE_STYLES,
  PROFITABLE_DAYS_CONSTANTS,
} from '../constants/objective-constants';
import { ObjectiveData } from '../types';
import {
  getConsistencyTarget,
  getCurrentInactiveDays,
  getCurrentProfitableDays,
  getInactivityLimit,
} from '../utils/objective-calculations';
import { BaseObjectiveProps, useObjectiveBase } from './base';
import {
  type ProfitableDayEntry,
  ProfitableDaysModal,
} from './profitable-days-modal';

interface ProfitableDaysObjectiveProps extends BaseObjectiveProps {
  account?: Account;
  accountBalance?: AccountBalance;
  program: Program | undefined | null;
  profitableDays: ProfitableDaysResult | null | undefined;
}

export const ProfitableDaysObjective = ({
  accountBalance,
  program,
  profitableDays,
}: ProfitableDaysObjectiveProps): ObjectiveData => {
  const { t, theme, valueProps, getMainTextStyle, getSecondaryTextStyle } =
    useObjectiveBase();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isMobile } = useBreakpoints();

  const profitableDaysTarget =
    getConsistencyTarget(program) ?? DEFAULT_VALUES.CONSISTENCY_TARGET;

  const profitableDayEntries = useMemo<ProfitableDayEntry[]>(() => {
    const entries = profitableDays?.profitableDays ?? [];

    return entries.map((entry) => ({
      date: dayjs(entry).toISOString(),
    }));
  }, [profitableDays]);

  const currentProfitableDays = useMemo(
    () => getCurrentProfitableDays(profitableDays),
    [profitableDays],
  );

  const currentInactiveDays = useMemo(
    () => getCurrentInactiveDays(accountBalance),
    [accountBalance],
  );

  const inactivityLimit = useMemo(() => getInactivityLimit(program), [program]);

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const inactiveDaysSection = (
    <Box sx={OBJECTIVE_STYLES.INACTIVE_DAYS_CONTAINER}>
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.second,
          fontSize: {
            xs: theme.typography.pxToRem(12),
            sm: theme.typography.pxToRem(14),
          },
        }}
      >
        {t('objectives.inactiveDays.label')}{' '}
        <Box component="span" sx={OBJECTIVE_STYLES.BOLD_TEXT}>
          {currentInactiveDays}
        </Box>{' '}
        / {inactivityLimit}
      </Typography>
    </Box>
  );

  const tooltipContent = (
    <Stack gap={2}>
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.opposite,
          lineHeight: theme.typography.body2.lineHeight,
        }}
      >
        {t('objectives.profitableDays.tooltip')}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.opposite,
          lineHeight: theme.typography.body2.lineHeight,
          marginTop: theme.spacing(1),
        }}
      >
        {t('objectives.profitableDays.tooltipExplanation')}
      </Typography>
      <Button
        variant="outlined"
        size="sm"
        onClick={handleOpenModal}
        sx={{
          alignSelf: 'flex-start',
          paddingInline: theme.spacing(4),
          color: theme.palette.secondary.main,
          borderColor: theme.palette.secondary.main,
          '&:hover': {
            color: theme.palette.secondary.main,
            borderColor: theme.palette.secondary.main,
            backgroundColor: 'transparent',
          },
        }}
      >
        {t('objectives.profitableDays.cta')}
      </Button>
    </Stack>
  );

  const primaryLabel = (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(1),
      }}
    >
      <Typography
        component="span"
        variant="body2"
        sx={{ color: theme.palette.text.second }}
      >
        {t('objectives.profitableDays.label')}
      </Typography>
      {isMobile ? (
        <IconButton
          onClick={handleOpenModal}
          sx={{
            color: theme.palette.primary.main,
          }}
        >
          <SystemIcon
            iconName={OBJECTIVE_ICONS.INFO}
            size={PROFITABLE_DAYS_CONSTANTS.INFO_ICON_SIZE}
            sx={{
              opacity: profitableDayEntries.length > 0 ? 1 : 0.6,
            }}
          />
        </IconButton>
      ) : (
        <Tooltip
          title={tooltipContent}
          slotProps={{
            tooltip: {
              sx: {
                maxWidth: PROFITABLE_DAYS_CONSTANTS.TOOLTIP_MAX_WIDTH,
              },
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              sx={{
                color: theme.palette.primary.main,
              }}
            >
              <SystemIcon
                iconName={OBJECTIVE_ICONS.INFO}
                size={PROFITABLE_DAYS_CONSTANTS.INFO_ICON_SIZE}
                sx={{
                  opacity: profitableDayEntries.length > 0 ? 1 : 0.6,
                }}
              />
            </IconButton>
          </Box>
        </Tooltip>
      )}
    </Box>
  );

  return {
    icon: (
      <SystemIcon
        iconName={OBJECTIVE_ICONS.INFO}
        size={DEFAULT_VALUES.OBJECTIVE_ICON_SIZE}
      />
    ),
    primaryText: primaryLabel,
    secondaryText: (
      <>
        <Box>
          {profitableDaysTarget ? (
            <>
              <Typography
                variant="subtitle1"
                sx={getMainTextStyle}
                {...valueProps}
              >
                {currentProfitableDays}
              </Typography>
              <Typography variant="body2" sx={getSecondaryTextStyle}>
                {t('objectives.profitableDays.total', {
                  total: profitableDaysTarget,
                })}
              </Typography>
            </>
          ) : null}
        </Box>
        {inactiveDaysSection}
        <ProfitableDaysModal
          open={isModalOpen}
          onClose={handleCloseModal}
          profitableDays={profitableDayEntries}
        />
      </>
    ),
  };
};
